import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ZAxis, Cell, ReferenceLine, ReferenceArea, ComposedChart, Bar, Line } from 'recharts';
import { Search, RefreshCw, Grid3X3, TrendingUp, AlertTriangle, Package, BarChart3, SlidersHorizontal, HelpCircle, Scissors } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Tooltip as UiTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FilterPanel } from '@/components/FilterPanel';
import { HelpBubble } from '@/components/HelpBubble';
import { ExportDropdown } from '@/components/ExportDropdown';
import { downloadCSV, buildPDF } from '@/lib/exportUtils';
import { useUser } from '@/lib/UserContext';
import api from '@/lib/api';
import { AIPanel } from '@/components/AIPanel';
import { toast } from 'sonner';
import { useCurrency } from '@/lib/useCurrency';
import { useGlobalFilters } from '@/lib/FilterContext';
import { useSearchParams } from 'react-router-dom';
import { TailSpendTab } from '@/components/spendDistribution/TailSpendTab';

const QUADRANT_COLORS = {
  Strategic: '#22c55e',    // Green
  Leverage: '#3b82f6',     // Blue  
  Bottleneck: '#f59e0b',   // Amber
  'Non-Critical': '#94a3b8' // Gray
};

const MATRIX_TYPES = [
  { value: 'spend_vs_transactions', label: 'Spend vs Transactions', xLabel: 'Transaction Volume', yLabel: 'Spend' },
  { value: 'spend_vs_risk', label: 'Spend vs Risk Rating', xLabel: 'Risk Level', yLabel: 'Spend' },
  { value: 'spend_vs_invoices', label: 'Spend vs Invoice Volume', xLabel: 'Invoice Count', yLabel: 'Spend' }
];

const PARETO_BAR_COLOR = '#3b82f6';
const PARETO_LINE_COLOR = '#ef4444';

function ParetoAnalysis({ distribution, totalSpend, totalSuppliers }) {
  const [paretoPage, setParetoPage] = useState(0);
  const PAGE_SIZE = 10;
  const { symbol: cs } = useCurrency();

  if (!distribution || distribution.length === 0) {
    return <p className="text-center text-muted-foreground py-8">No data available</p>;
  }

  // Build pareto data — already sorted by spend descending
  let cumulative = 0;
  const paretoFull = distribution.map((d, i) => {
    cumulative += d.total_spend;
    return {
      rank: i + 1,
      supplier: d.supplier,
      spend: d.total_spend,
      cumulative_pct: totalSpend > 0 ? (cumulative / totalSpend) * 100 : 0,
      supplier_pct: ((i + 1) / totalSuppliers) * 100,
    };
  });

  // Find key crossover points
  const pct80 = paretoFull.find(p => p.cumulative_pct >= 80);
  const pct50 = paretoFull.find(p => p.cumulative_pct >= 50);
  const suppliersFor80 = pct80?.rank || totalSuppliers;
  const suppliersFor50 = pct50?.rank || totalSuppliers;
  const vitalFewSpend = paretoFull.slice(0, suppliersFor80).reduce((s, p) => s + p.spend, 0);
  const trivialManyCount = totalSuppliers - suppliersFor80;

  // Paginated table data — 10 per page, descending
  const totalPages = Math.ceil(paretoFull.length / PAGE_SIZE);
  const pagedData = paretoFull.slice(paretoPage * PAGE_SIZE, (paretoPage + 1) * PAGE_SIZE);

  // Chart shows top N suppliers (max 40 for readability)
  const chartMax = Math.min(40, paretoFull.length);
  const chartData = paretoFull.slice(0, chartMax);

  return (
    <>
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">80% SPEND COVERED BY <HelpBubble text="Number of suppliers responsible for 80% of total spend. The 'vital few' — where strategic sourcing effort pays back the most." /></CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold font-mono" data-testid="pareto-80-count">{suppliersFor80}</div>
            <p className="text-xs text-muted-foreground mt-1">
              of {totalSuppliers} suppliers ({((suppliersFor80 / totalSuppliers) * 100).toFixed(1)}%)
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">50% SPEND COVERED BY <HelpBubble text="Number of suppliers responsible for 50% of spend. If very small, you have high concentration risk — negotiate hard but consider dual-sourcing." /></CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold font-mono" data-testid="pareto-50-count">{suppliersFor50}</div>
            <p className="text-xs text-muted-foreground mt-1">
              of {totalSuppliers} suppliers ({((suppliersFor50 / totalSuppliers) * 100).toFixed(1)}%)
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">VITAL FEW AVG SPEND <HelpBubble text="Average spend per supplier in the top-80% tier. Compare to Trivial Many below — a large gap confirms the classic Pareto pattern." /></CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold font-mono" data-testid="pareto-vital-avg">
              {cs}{suppliersFor80 > 0 ? Math.round(vitalFewSpend / suppliersFor80).toLocaleString() : 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Top {suppliersFor80} suppliers</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">TRIVIAL MANY <HelpBubble text="Suppliers making up the bottom 20% of spend. Prime targets for tail-spend consolidation, e-catalogue buying, or supplier rationalisation." /></CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold font-mono" data-testid="pareto-trivial-count">{trivialManyCount}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {totalSpend > 0 ? (((totalSpend - vitalFewSpend) / totalSpend) * 100).toFixed(1) : 0}% of total spend
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Pareto Chart */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Card data-testid="pareto-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Pareto Chart — Supplier Spend Concentration
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Top {chartMax} suppliers by spend. The red line shows cumulative % of total spend.
            </p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={340}>
              <ComposedChart data={chartData} margin={{ top: 10, right: 40, bottom: 80, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="supplier"
                  stroke="hsl(var(--muted-foreground))"
                  angle={-45}
                  textAnchor="end"
                  interval={0}
                  tick={{ fontSize: 9 }}
                  height={90}
                  tickFormatter={(v) => v.length > 14 ? v.slice(0, 12) + '...' : v}
                />
                <YAxis
                  yAxisId="spend"
                  stroke="hsl(var(--muted-foreground))"
                  tickFormatter={(v) => v >= 1000000 ? `${cs}${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${cs}${(v / 1000).toFixed(0)}K` : `${cs}${v}`}
                  label={{ value: 'Spend', angle: -90, position: 'insideLeft', offset: -5, style: { fontSize: 11 } }}
                />
                <YAxis
                  yAxisId="pct"
                  orientation="right"
                  domain={[0, 100]}
                  stroke={PARETO_LINE_COLOR}
                  tickFormatter={(v) => `${v}%`}
                  label={{ value: 'Cumulative %', angle: 90, position: 'insideRight', offset: 10, style: { fontSize: 11, fill: PARETO_LINE_COLOR } }}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const d = payload[0]?.payload;
                      return (
                        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg text-sm">
                          <p className="font-semibold mb-1 truncate max-w-[200px]">{d.supplier}</p>
                          <p><span className="text-muted-foreground">Rank:</span> #{d.rank}</p>
                          <p><span className="text-muted-foreground">Spend:</span> {cs}{d.spend?.toLocaleString()}</p>
                          <p><span className="text-muted-foreground">Cumulative:</span> {d.cumulative_pct?.toFixed(1)}%</p>
                          <p><span className="text-muted-foreground">Supplier %:</span> {d.supplier_pct?.toFixed(1)}% of all</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar yAxisId="spend" dataKey="spend" fill={PARETO_BAR_COLOR} fillOpacity={0.8} radius={[2, 2, 0, 0]} />
                <Line yAxisId="pct" type="monotone" dataKey="cumulative_pct" stroke={PARETO_LINE_COLOR} strokeWidth={2.5} dot={false} />
                <ReferenceLine yAxisId="pct" y={80} stroke={PARETO_LINE_COLOR} strokeDasharray="6 3" strokeOpacity={0.6} label={{ value: '80%', position: 'right', fill: PARETO_LINE_COLOR, fontSize: 11 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </motion.div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between px-2" data-testid="pareto-pagination">
        <p className="text-sm text-muted-foreground">
          Page {paretoPage + 1} of {totalPages}
        </p>
        <div className="flex gap-2">
          <button
            data-testid="pareto-prev-btn"
            onClick={() => setParetoPage(p => Math.max(0, p - 1))}
            disabled={paretoPage === 0}
            className="px-4 py-2 text-sm font-medium rounded-md border border-border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <button
            data-testid="pareto-next-btn"
            onClick={() => setParetoPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={paretoPage >= totalPages - 1}
            className="px-4 py-2 text-sm font-medium rounded-md border border-border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </div>

      {/* Pareto Table */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card data-testid="pareto-table">
          <CardHeader>
            <CardTitle>Pareto Ranking — Top Suppliers</CardTitle>
            <p className="text-sm text-muted-foreground">Sorted by spend descending with cumulative contribution</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Rank</th>
                    <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Supplier</th>
                    <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Spend</th>
                    <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">% of Total</th>
                    <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Cumulative %</th>
                    <th className="text-center py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Class</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedData.map((p) => (
                    <tr key={p.rank} className={`border-b border-border hover:bg-muted/50 transition-colors ${p.rank === suppliersFor80 ? 'bg-red-50 dark:bg-red-950/20' : ''}`}>
                      <td className="py-1.5 px-3 font-mono text-sm">{p.rank}</td>
                      <td className="py-1.5 px-3 font-medium truncate max-w-[200px]">{p.supplier}</td>
                      <td className="py-1.5 px-3 text-right font-mono">{cs}{p.spend?.toLocaleString()}</td>
                      <td className="py-1.5 px-3 text-right">{totalSpend > 0 ? ((p.spend / totalSpend) * 100).toFixed(2) : 0}%</td>
                      <td className="py-1.5 px-3 text-right font-mono">{p.cumulative_pct.toFixed(1)}%</td>
                      <td className="py-1.5 px-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          p.cumulative_pct <= 80 ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        }`}>
                          {p.cumulative_pct <= 80 ? 'Vital Few' : 'Trivial Many'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </>
  );
}

export default function SpendDistribution() {
  const { symbol: cs } = useCurrency();
  const user = useUser();
  const { filters, setFilters, kraljicThreshold, setKraljicThreshold } = useGlobalFilters();
  const [data, setData] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [kraljicData, setKraljicData] = useState(null);
  const [selectedMatrixType, setSelectedMatrixType] = useState('spend_vs_transactions');

  // Reclassify suppliers into quadrants based on the threshold slider
  const reclassified = useMemo(() => {
    if (!kraljicData?.data?.length) return { data: [], counts: {}, spend: {}, xThreshold: 0, yThreshold: 0 };
    const sorted_x = kraljicData.data.map(d => d.x_value).sort((a, b) => a - b);
    const sorted_y = kraljicData.data.map(d => d.y_value).sort((a, b) => a - b);
    const pctIdx = (arr) => Math.min(Math.floor(arr.length * kraljicThreshold / 100), arr.length - 1);
    const xT = sorted_x[pctIdx(sorted_x)] || 0;
    const yT = sorted_y[pctIdx(sorted_y)] || 0;

    // Build rank maps for percentile positioning
    const xRankMap = new Map();
    sorted_x.forEach((v, i) => { if (!xRankMap.has(v)) xRankMap.set(v, i); });
    const yRankMap = new Map();
    sorted_y.forEach((v, i) => { if (!yRankMap.has(v)) yRankMap.set(v, i); });
    const n = kraljicData.data.length || 1;

    const counts = { Strategic: 0, Leverage: 0, Bottleneck: 0, 'Non-Critical': 0 };
    const spend = { Strategic: 0, Leverage: 0, Bottleneck: 0, 'Non-Critical': 0 };

    const data = kraljicData.data.map(d => {
      let q;
      if (d.x_value >= xT && d.y_value >= yT) q = 'Strategic';
      else if (d.x_value < xT && d.y_value >= yT) q = 'Leverage';
      else if (d.x_value >= xT && d.y_value < yT) q = 'Bottleneck';
      else q = 'Non-Critical';
      counts[q]++;
      spend[q] += d.spend || 0;
      return {
        ...d, quadrant: q,
        x_pct: ((xRankMap.get(d.x_value) || 0) / n) * 100,
        y_pct: ((yRankMap.get(d.y_value) || 0) / n) * 100,
      };
    });

    const thresholdLine = kraljicThreshold;

    return { data, counts, spend, xThreshold: xT, yThreshold: yT, thresholdLine };
  }, [kraljicData, kraljicThreshold]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = ['pareto', 'distribution', 'tail'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'kraljic';
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const buildQueryString = useCallback((filterObj) => {
    const params = new URLSearchParams();
    if (filterObj.category) params.append('category', filterObj.category);
    if (filterObj.category_l2) params.append('category_l2', filterObj.category_l2);
    if (filterObj.category_l3) params.append('category_l3', filterObj.category_l3);
    if (filterObj.region) params.append('region', filterObj.region);
    if (filterObj.country) params.append('country', filterObj.country);
    if (filterObj.sbu) params.append('sbu', filterObj.sbu);
    if (filterObj.cost_center) params.append('cost_center', filterObj.cost_center);
    if (filterObj.business_unit) params.append('business_unit', filterObj.business_unit);
    if (filterObj.addressability) params.append('addressability', filterObj.addressability);
    if (filterObj.fiscal_year) params.append('fiscal_year', filterObj.fiscal_year);
    if (filterObj.startDate) params.append('start_date', filterObj.startDate);
    if (filterObj.endDate) params.append('end_date', filterObj.endDate);
    return params.toString();
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchKraljicData();
  }, [selectedMatrixType, filters]);

  useEffect(() => {
    if (data) {
      filterLocalData();
    }
  }, [searchTerm, data]);

  const fetchData = async (filterObj = filters) => {
    try {
      const queryString = buildQueryString(filterObj);
      const response = await api.get(`/analytics/spend-distribution${queryString ? `?${queryString}` : ''}`);
      setData(response.data);
      setFilteredData(response.data.distribution);
    } catch (error) {
      toast.error('Failed to load spend distribution');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchKraljicData = async () => {
    try {
      const queryString = buildQueryString(filters);
      const matrixQuery = queryString ? `${queryString}&matrix_type=${selectedMatrixType}` : `matrix_type=${selectedMatrixType}`;
      const response = await api.get(`/analytics/kraljic-matrix?${matrixQuery}`);
      setKraljicData(response.data);
    } catch (error) {
      console.error('Failed to load Kraljic matrix data');
    }
  };

  const filterLocalData = () => {
    if (!searchTerm) {
      setFilteredData(data.distribution);
      return;
    }

    const filtered = data.distribution.filter((item) =>
      item.supplier.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(filtered);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setRefreshing(true);
    fetchData(newFilters);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData(filters);
    fetchKraljicData();
  };

  const handleMatrixTypeChange = (value) => {
    setSelectedMatrixType(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const activeFiltersCount = Object.values(filters).filter(v => v !== null && v !== '' && v !== 'all').length;
  const currentMatrixConfig = MATRIX_TYPES.find(m => m.value === selectedMatrixType);

  const exportCSV = () => {
    // Build Pareto data for export
    let cumulative = 0;
    const paretoRows = (data?.distribution || []).map((d, i) => {
      cumulative += d.total_spend;
      const cPct = data?.total_spend > 0 ? ((cumulative / data.total_spend) * 100).toFixed(2) : 0;
      return [i + 1, d.supplier, d.total_spend, `${d.spend_percentage?.toFixed(2)}%`, `${cPct}%`, parseFloat(cPct) <= 80 ? 'Vital Few' : 'Trivial Many'];
    });

    const rows = [
      ['--- Summary ---'],
      ['Total Suppliers', data?.total_suppliers],
      ['Total Spend', `${cs}${data?.total_spend?.toLocaleString()}`],
      [],
      ['--- Kraljic Matrix ---'],
      ['Supplier', 'Quadrant', 'Spend', 'Transactions', 'Invoices', 'Risk'],
      ...(kraljicData?.data || []).map(k => [k.supplier, k.quadrant, k.spend, k.transaction_count, k.invoice_count, k.risk_rating]),
      [],
      ['--- Pareto Analysis ---'],
      ['Rank', 'Supplier', 'Spend', '% of Total', 'Cumulative %', 'Class'],
      ...paretoRows,
      [],
      ['--- Distribution Analysis ---'],
      ['Supplier', 'Total Spend', '% of Total', 'Transactions', 'Categories', 'Locations'],
      ...(filteredData || []).map(d => [d.supplier, d.total_spend, `${d.spend_percentage?.toFixed(2)}%`, d.transaction_count, d.category_count, d.department_count]),
    ];
    downloadCSV(rows, 'spend_distribution');
  };

  const exportPDF = () => {
    const sections = [
      { title: 'Summary', headers: ['Metric', 'Value'], rows: [['Total Suppliers', String(data?.total_suppliers)], ['Total Spend', `${cs}${data?.total_spend?.toLocaleString()}`], ['Avg Spend/Supplier', `${cs}${data?.total_suppliers > 0 ? Math.round(data?.total_spend / data?.total_suppliers).toLocaleString() : 0}`]] },
    ];
    if (kraljicData?.data?.length > 0) {
      sections.push({ title: 'Kraljic Matrix', headers: ['Supplier', 'Quadrant', 'Spend', 'Transactions', 'Risk'], rows: kraljicData.data.slice(0, 50).map(k => [k.supplier, k.quadrant, `${cs}${k.spend?.toLocaleString()}`, String(k.transaction_count), k.risk_rating]) });
    }
    sections.push({ title: 'Distribution Analysis', headers: ['Supplier', 'Total Spend', '% of Total', 'Transactions'], rows: (filteredData || []).slice(0, 50).map(d => [d.supplier, `${cs}${d.total_spend?.toLocaleString()}`, `${d.spend_percentage?.toFixed(2)}%`, String(d.transaction_count)]) });
    buildPDF(sections, 'Spend_Distribution');
  };

  return (
    <div className="space-y-4" data-testid="spend-distribution-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Spend Distribution</h1>
          <p className="text-muted-foreground mt-1">
            Multi-dimensional spend analysis across suppliers
            {activeFiltersCount > 0 && (
              <span className="ml-2 text-primary font-medium">
                ({activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} applied)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportDropdown onExportCSV={exportCSV} onExportPDF={exportPDF} />
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh} 
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filter Panel */}
      <FilterPanel
        filters={filters}
        onFiltersChange={handleFiltersChange}
        pageName="spend-distribution"
        visibleFilters={['category', 'category_l2', 'category_l3', 'supplier', 'region', 'country', 'sbu', 'fiscal_year', 'cost_center', 'business_unit']}
        showDateRange={true}
        compact={true}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">TOTAL SPEND</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold font-mono">{cs}{data?.total_spend?.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">TOTAL SUPPLIERS</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold font-mono">{data?.total_suppliers}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">AVG SPEND/SUPPLIER</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold font-mono">
              {cs}{data?.total_suppliers > 0 ? Math.round(data?.total_spend / data?.total_suppliers).toLocaleString() : 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different views */}
      <Tabs value={activeTab} onValueChange={(v) => setSearchParams(v === 'kraljic' ? {} : { tab: v }, { replace: true })} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="kraljic" data-testid="kraljic-tab">
            <Grid3X3 className="w-4 h-4 mr-2" />
            Kraljic Matrix
          </TabsTrigger>
          <TabsTrigger value="pareto" data-testid="pareto-tab">
            <BarChart3 className="w-4 h-4 mr-2" />
            Pareto Analysis
          </TabsTrigger>
          <TabsTrigger value="distribution" data-testid="distribution-tab">
            <TrendingUp className="w-4 h-4 mr-2" />
            Distribution Analysis
          </TabsTrigger>
          <TabsTrigger value="tail" data-testid="tail-spend-tab-trigger">
            <Scissors className="w-4 h-4 mr-2" />
            Tail Spend
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tail">
          {activeTab === 'tail' && <TailSpendTab queryString={buildQueryString(filters)} cs={cs} />}
        </TabsContent>

        {/* Kraljic Matrix Tab */}
        <TabsContent value="kraljic" className="space-y-4">
          {/* Quadrant Summary Cards */}
          {kraljicData && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-l-4" style={{ borderLeftColor: QUADRANT_COLORS.Strategic }}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" style={{ color: QUADRANT_COLORS.Strategic }} />
                    Strategic
                    <HelpBubble text="High spend + high supply risk. Few alternative suppliers. Manage with long-term partnerships, joint innovation, dual-sourcing where possible, and executive sponsorship." />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-lg font-bold font-mono">{reclassified.counts?.Strategic || 0}</div>
                  <p className="text-xs text-muted-foreground">{cs}{(reclassified.spend?.Strategic || 0).toLocaleString()}</p>
                </CardContent>
              </Card>
              
              <Card className="border-l-4" style={{ borderLeftColor: QUADRANT_COLORS.Leverage }}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Package className="w-4 h-4" style={{ color: QUADRANT_COLORS.Leverage }} />
                    Leverage
                    <HelpBubble text="High spend + low supply risk. Many alternative suppliers, so you have negotiating power. Use e-auctions, competitive bidding, and consolidation to squeeze cost." />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-lg font-bold font-mono">{reclassified.counts?.Leverage || 0}</div>
                  <p className="text-xs text-muted-foreground">{cs}{(reclassified.spend?.Leverage || 0).toLocaleString()}</p>
                </CardContent>
              </Card>
              
              <Card className="border-l-4" style={{ borderLeftColor: QUADRANT_COLORS.Bottleneck }}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" style={{ color: QUADRANT_COLORS.Bottleneck }} />
                    Bottleneck
                    <HelpBubble text="Low spend + high supply risk. Small in £ terms but a stock-out can halt operations. Focus on secured contracts, safety stock, and qualifying alternative sources." />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-lg font-bold font-mono">{reclassified.counts?.Bottleneck || 0}</div>
                  <p className="text-xs text-muted-foreground">{cs}{(reclassified.spend?.Bottleneck || 0).toLocaleString()}</p>
                </CardContent>
              </Card>
              
              <Card className="border-l-4" style={{ borderLeftColor: QUADRANT_COLORS['Non-Critical'] }}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Grid3X3 className="w-4 h-4" style={{ color: QUADRANT_COLORS['Non-Critical'] }} />
                    Non-Critical
                    <HelpBubble text="Low spend + low supply risk. Standard commodities. Automate with e-catalogues, P-cards, or catalogue punch-out; free procurement effort for higher-value quadrants." />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-lg font-bold font-mono">{reclassified.counts?.['Non-Critical'] || 0}</div>
                  <p className="text-xs text-muted-foreground">{cs}{(reclassified.spend?.['Non-Critical'] || 0).toLocaleString()}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Kraljic Matrix Chart */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card data-testid="kraljic-matrix-chart">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Grid3X3 className="w-5 h-5" />
                      Kraljic Matrix: {currentMatrixConfig?.label}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Suppliers positioned by {currentMatrixConfig?.xLabel} (X) and {currentMatrixConfig?.yLabel} (Y)
                    </p>
                  </div>
                  <Select value={selectedMatrixType} onValueChange={handleMatrixTypeChange}>
                    <SelectTrigger className="w-full md:w-[220px] shrink-0" data-testid="matrix-type-select">
                      <SelectValue placeholder="Select matrix type" />
                    </SelectTrigger>
                    <SelectContent>
                      {MATRIX_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {reclassified.data.length > 0 ? (
                  <div className="relative">
                    {/* Threshold Slider */}
                    <div className="flex items-center gap-4 mb-4 px-2 py-3 bg-muted/50 rounded-lg" data-testid="kraljic-threshold-slider">
                      <SlidersHorizontal className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">Threshold</span>
                      <Slider
                        value={[kraljicThreshold]}
                        onValueChange={(v) => setKraljicThreshold(v[0])}
                        min={10}
                        max={90}
                        step={5}
                        className="flex-1"
                      />
                      <span className="text-xs font-mono font-semibold w-10 text-right">{kraljicThreshold}%</span>
                    </div>

                    
                    <ResponsiveContainer width="100%" height={500}>
                      <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis
                          type="number"
                          dataKey="x_pct"
                          domain={[0, 100]}
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(v) => `${Math.round(v)}%`}
                          label={{ value: `${currentMatrixConfig?.xLabel} (Percentile)`, position: 'insideBottom', offset: -10, fontSize: 11 }}
                        />
                        <YAxis
                          type="number"
                          dataKey="y_pct"
                          domain={[0, 100]}
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(v) => `${Math.round(v)}%`}
                          label={{ value: `${currentMatrixConfig?.yLabel} (Percentile)`, angle: -90, position: 'insideLeft', offset: 10, fontSize: 11 }}
                        />
                        <ZAxis type="number" dataKey="transaction_count" range={[50, 400]} name="Transactions" />
                        
                        {/* Colored quadrant backgrounds */}
                        <ReferenceArea x1={0} x2={reclassified.thresholdLine} y1={reclassified.thresholdLine} y2={100} fill="#3b82f6" fillOpacity={0.07} label={{ value: 'LEVERAGE', position: 'insideTopLeft', fontSize: 12, fill: '#3b82f6', fontWeight: 700 }} />
                        <ReferenceArea x1={reclassified.thresholdLine} x2={100} y1={reclassified.thresholdLine} y2={100} fill="#22c55e" fillOpacity={0.07} label={{ value: 'STRATEGIC', position: 'insideTopRight', fontSize: 12, fill: '#22c55e', fontWeight: 700 }} />
                        <ReferenceArea x1={0} x2={reclassified.thresholdLine} y1={0} y2={reclassified.thresholdLine} fill="#94a3b8" fillOpacity={0.07} label={{ value: 'NON-CRITICAL', position: 'insideBottomLeft', fontSize: 12, fill: '#94a3b8', fontWeight: 700 }} />
                        <ReferenceArea x1={reclassified.thresholdLine} x2={100} y1={0} y2={reclassified.thresholdLine} fill="#f59e0b" fillOpacity={0.07} label={{ value: 'BOTTLENECK', position: 'insideBottomRight', fontSize: 12, fill: '#f59e0b', fontWeight: 700 }} />

                        {/* Reference lines at percentile thresholds */}
                        <ReferenceLine 
                          x={reclassified.thresholdLine} 
                          stroke="#6366f1"
                          strokeWidth={2}
                          strokeDasharray="6 4"
                          label={{ value: `P${kraljicThreshold}`, position: 'top', fontSize: 11, fill: '#6366f1', fontWeight: 600 }}
                        />
                        <ReferenceLine 
                          y={reclassified.thresholdLine} 
                          stroke="#6366f1"
                          strokeWidth={2}
                          strokeDasharray="6 4"
                          label={{ value: `P${kraljicThreshold}`, position: 'right', fontSize: 11, fill: '#6366f1', fontWeight: 600 }}
                        />
                        
                        <Tooltip
                          cursor={{ strokeDasharray: '3 3' }}
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const item = payload[0].payload;
                              return (
                                <div className="bg-popover border border-border rounded-lg p-3 shadow-lg max-w-xs">
                                  <p className="font-semibold mb-2 truncate">{item.supplier}</p>
                                  <div className="space-y-1 text-sm">
                                    <p>
                                      <span className="text-muted-foreground">Quadrant:</span>{' '}
                                      <span className="font-medium" style={{ color: QUADRANT_COLORS[item.quadrant] }}>
                                        {item.quadrant}
                                      </span>
                                    </p>
                                    <p><span className="text-muted-foreground">Spend:</span> {cs}{item.spend?.toLocaleString()}</p>
                                    <p><span className="text-muted-foreground">Transactions:</span> {item.transaction_count}</p>
                                    <p><span className="text-muted-foreground">Invoices:</span> {item.invoice_count}</p>
                                    <p><span className="text-muted-foreground">Risk:</span> {item.risk_rating}</p>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Scatter key={`scatter-${kraljicThreshold}`} name="Suppliers" data={reclassified.data}>
                          {reclassified.data.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={QUADRANT_COLORS[entry.quadrant]}
                              fillOpacity={0.7}
                            />
                          ))}
                        </Scatter>
                      </ScatterChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No data available</p>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Suppliers by Quadrant Table */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card data-testid="quadrant-table">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CardTitle>Suppliers by Quadrant</CardTitle>
                  <TooltipProvider delayDuration={200}>
                    <UiTooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs text-sm leading-relaxed">
                        <p className="font-semibold mb-1">Kraljic Quadrant Classification</p>
                        <p><span className="font-medium text-green-600">Strategic:</span> High spend, high complexity — invest in partnerships</p>
                        <p><span className="font-medium text-blue-600">Leverage:</span> High spend, low complexity — negotiate aggressively</p>
                        <p><span className="font-medium text-amber-600">Bottleneck:</span> Low spend, high complexity — secure supply</p>
                        <p><span className="font-medium text-gray-500">Non-Critical:</span> Low spend, low complexity — simplify &amp; automate</p>
                      </TooltipContent>
                    </UiTooltip>
                  </TooltipProvider>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Supplier</th>
                        <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Quadrant</th>
                        <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Spend</th>
                        <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Transactions</th>
                        <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Invoices</th>
                        <th className="text-center py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">Risk</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Show top suppliers from each quadrant for balanced view */}
                      {(() => {
                        const quadrants = ['Strategic', 'Leverage', 'Bottleneck', 'Non-Critical'];
                        const perQuadrant = 8;
                        const rows = [];
                        for (const q of quadrants) {
                          const items = reclassified.data
                            .filter(d => d.quadrant === q)
                            .sort((a, b) => (b.spend || 0) - (a.spend || 0))
                            .slice(0, perQuadrant);
                          rows.push(...items);
                        }
                        return rows.map((item, idx) => (
                          <tr key={idx} className="border-b border-border hover:bg-muted/50 transition-colors">
                            <td className="py-1.5 px-3 font-medium truncate max-w-[200px]">{item.supplier}</td>
                            <td className="py-1.5 px-3">
                              <span 
                                className="px-2 py-1 rounded-full text-xs font-medium text-white"
                                style={{ backgroundColor: QUADRANT_COLORS[item.quadrant] }}
                              >
                                {item.quadrant}
                              </span>
                            </td>
                            <td className="py-1.5 px-3 text-right font-mono">{cs}{item.spend?.toLocaleString()}</td>
                            <td className="py-1.5 px-3 text-right">{item.transaction_count}</td>
                            <td className="py-1.5 px-3 text-right">{item.invoice_count}</td>
                            <td className="py-1.5 px-3 text-center">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                item.risk_rating === 'High' ? 'bg-red-100 text-red-700' :
                                item.risk_rating === 'Medium' ? 'bg-amber-100 text-amber-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {item.risk_rating}
                              </span>
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                  {reclassified.data.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No data available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Pareto Analysis Tab */}
        <TabsContent value="pareto" className="space-y-4">
          <ParetoAnalysis distribution={data?.distribution || []} totalSpend={data?.total_spend || 0} totalSuppliers={data?.total_suppliers || 0} />
        </TabsContent>

        {/* Distribution Analysis Tab */}
        <TabsContent value="distribution" className="space-y-4">
          {/* Search */}
          <Card>
            <CardContent className="pt-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search suppliers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="supplier-search"
                />
              </div>
            </CardContent>
          </Card>

          {/* Scatter Chart */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card data-testid="scatter-chart">
              <CardHeader>
                <CardTitle>Spend vs Transaction Volume</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={500}>
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      type="number"
                      dataKey="transaction_count"
                      name="Transactions"
                      stroke="hsl(var(--muted-foreground))"
                      label={{ value: 'Number of Transactions', position: 'insideBottom', offset: -5 }}
                    />
                    <YAxis
                      type="number"
                      dataKey="total_spend"
                      name="Spend"
                      stroke="hsl(var(--muted-foreground))"
                      tickFormatter={(v) => `${cs}${(v/1000000).toFixed(1)}M`}
                      label={{ value: 'Total Spend', angle: -90, position: 'insideLeft' }}
                    />
                    <ZAxis type="number" dataKey="category_count" range={[50, 500]} name="Categories" />
                    <Tooltip
                      cursor={{ strokeDasharray: '3 3' }}
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
                              <p className="font-semibold mb-1">{data.supplier}</p>
                              <p className="text-sm">Spend: {cs}{data.total_spend?.toLocaleString()}</p>
                              <p className="text-sm">Transactions: {data.transaction_count}</p>
                              <p className="text-sm">Categories: {data.category_count}</p>
                              <p className="text-sm">Locations: {data.department_count}</p>
                              <p className="text-sm text-muted-foreground mt-1">
                                Avg: {cs}{data.avg_transaction_value?.toLocaleString()}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Scatter name="Suppliers" data={filteredData} fill="hsl(var(--primary))" />
                  </ScatterChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </motion.div>

          {/* Detailed Table */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card data-testid="distribution-table">
              <CardHeader>
                <CardTitle>Supplier Distribution Details ({filteredData.length} suppliers)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">
                          Supplier
                        </th>
                        <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">
                          Total Spend
                        </th>
                        <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">
                          % of Total
                        </th>
                        <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">
                          Transactions
                        </th>
                        <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">
                          Categories
                        </th>
                        <th className="text-right py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase">
                          Locations
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.slice(0, 50).map((item) => (
                        <tr key={item.supplier} className="border-b border-border hover:bg-muted/50 transition-colors">
                          <td className="py-1.5 px-3 font-medium">{item.supplier}</td>
                          <td className="py-1.5 px-3 text-right font-mono">{cs}{item.total_spend?.toLocaleString()}</td>
                          <td className="py-1.5 px-3 text-right">{item.spend_percentage?.toFixed(2)}%</td>
                          <td className="py-1.5 px-3 text-right">{item.transaction_count}</td>
                          <td className="py-1.5 px-3 text-right">{item.category_count}</td>
                          <td className="py-1.5 px-3 text-right">{item.department_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredData.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No data available. Upload data to see insights.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
      </Tabs>

      <AIPanel page="spend-distribution" filters={filters} />
    </div>
  );
}
